
















































import { Editor as TipTapEditor, EditorContent, Extension } from '@tiptap/vue-2';
import History from '@tiptap/extension-history';
import Text from '@tiptap/extension-text';
import type { Emoji } from '@getaccept/lib-ui';
import { AvatarSize } from '@getaccept/lib-ui';
import {
  EnterBreakNode,
  InputDocNode,
  ParagraphNode,
  TextPlaceholder,
} from '@getaccept/editor-lib';
import type { PropType, Ref } from '@vue/composition-api';
import { defineComponent, onBeforeUnmount, onMounted, ref } from '@vue/composition-api';
import type { EmojiIndex } from 'emoji-mart-vue-fast';
import { EmojiHelper } from '../../emoji/helpers/emoji.helper';
import { ChatHelper } from '../../chat/helpers/chat.helper';
import type { CurrentUser } from '../types/current-user';
import { t } from '../../helpers/translation.helper';

export default defineComponent({
  components: {
    EditorContent,
  },
  props: {
    value: {
      type: String,
      required: true,
    },
    currentUser: {
      type: Object as PropType<CurrentUser>,
      default: () => ({ name: '', thumbUrl: '' }),
    },
    placeholder: {
      type: String,
      default: '',
    },
  },
  setup(props, { emit }) {
    const editor: Ref<TipTapEditor | null> = ref(null);
    const emojiIndex: Ref<EmojiIndex> = ref(null);
    const showEmojis = ref(false);

    const hasInput = ref(!!props.value);

    const setFocus = () => {
      if (editor.value?.view.hasFocus()) {
        return;
      }
      editor.value?.view.focus();
    };

    const handleInput = () => {
      const text = JSON.stringify(editor.value.getJSON());
      hasInput.value = !!editor.value.getText();

      if (text === props.value) {
        return;
      }

      emit('input', text);
    };

    const setShowEmojiPicker = (show: boolean) => {
      showEmojis.value = show;
    };

    const handleAddEmoji = ({ native }: Emoji) => {
      setShowEmojiPicker(false);
      editor.value.commands.insertContent(native);
      setFocus();
    };

    const handleSubmit = () => {
      if (!hasInput.value) {
        return;
      }

      emit('submit', JSON.stringify(editor.value.getJSON()));

      editor.value.commands.setContent('');
      hasInput.value = false;
    };

    const clickOutside = () => {
      setShowEmojiPicker(false);
    };

    onMounted(async () => {
      let content;

      // TODO: Delete all comments with plain text in DB, remove this try catch
      try {
        content = JSON.parse(props.value);
      } catch (e) {
        content = props.value;
      }

      editor.value = new TipTapEditor({
        content,
        enablePasteRules: false,
        extensions: [
          InputDocNode,
          ParagraphNode,
          Text,
          History,
          EnterBreakNode,
          TextPlaceholder.configure({ placeholder: props.placeholder }),
          Extension.create({
            addKeyboardShortcuts() {
              return {
                'Mod-Enter'() {
                  handleSubmit();
                  return true;
                },
              };
            },
          }),
        ],
        onUpdate: handleInput,
        onCreate: () => {
          editor.value?.view.focus();
        },
      });

      emojiIndex.value = await EmojiHelper.emojiIndex();
    });

    onBeforeUnmount(() => {
      editor.value.destroy();
    });

    return {
      AvatarSize,
      editor,
      emojiIndex,
      showEmojis,
      emojiLocalization: ChatHelper.getEmojiLocalization(),
      hasInput,
      clickOutside,
      setFocus,
      setShowEmojiPicker,
      handleAddEmoji,
      handleSubmit,
      t,
    };
  },
});
